import { useEffect, useState } from 'react';
import { SlotsScreen } from './SlotsScreen';
import { OlympusScreen } from './OlympusScreen';
import { DogHouseScreen } from './DogHouseScreen';
import { Icon } from '../components/Icon';
import { slotsApi, WinItem, ReplayResult, SlotGame } from '../api/slots';
import { useAppStore } from '../store/useAppStore';
import { formatTon } from '../lib/format';

interface GameCard {
  id: string;
  name: string;
  desc: string;
  ready: boolean;
  accent: string;
}

const GAMES: GameCard[] = [
  { id: 'olympus', name: 'Олимп · Гнев Богов', desc: 'Каскады + орбы-множители · 6×5 · 15 фриспинов', ready: true, accent: '#7a4dff' },
  { id: 'classic', name: 'Классика 777', desc: '5×3 · 10 линий · 5 тем · фриспины', ready: true, accent: '#00e676' },
  { id: 'doghouse', name: 'Дом Псов', desc: 'Sticky-wild ×2/×3 · 5×3 · 20 линий · фриспины', ready: true, accent: '#ff9e3d' },
  { id: 'megaways', name: 'Псы Megaways', desc: 'до 117 649 ways · скоро', ready: false, accent: '#18d6ff' },
];

const GAME_NAME: Record<SlotGame, string> = {
  olympus: 'Олимп',
  slots: 'Классика 777',
  doghouse: 'Дом Псов',
};

function ReplayList({ onBack, onOpen }: { onBack: () => void; onOpen: (r: ReplayResult) => void }) {
  const showToast = useAppStore((s) => s.showToast);
  const [wins, setWins] = useState<WinItem[] | null>(null);
  const [loadingId, setLoadingId] = useState<number | null>(null);

  useEffect(() => {
    slotsApi.wins(30).then(setWins).catch(() => setWins([]));
  }, []);

  const open = async (betId: number) => {
    setLoadingId(betId);
    try {
      const r = await slotsApi.replay(betId);
      onOpen(r);
    } catch {
      showToast('Не удалось загрузить повтор', true);
    } finally {
      setLoadingId(null);
    }
  };

  return (
    <>
      <div className="page-head">
        <button className="icon-btn" onClick={onBack} title="Назад">
          <Icon name="back" size={18} />
        </button>
        <span className="title">Повторы выигрышей</span>
      </div>
      <div className="stack">
        {wins === null && <div className="muted" style={{ padding: 16 }}>Загрузка…</div>}
        {wins !== null && wins.length === 0 && (
          <div className="muted" style={{ padding: 16 }}>Пока нет выигрышей — крути слоты!</div>
        )}
        {wins?.map((w) => (
          <button key={w.betId} className="replay-row" disabled={loadingId !== null} onClick={() => open(w.betId)}>
            <span className="replay-row-game">{GAME_NAME[w.game] ?? w.game}</span>
            <span className="replay-row-win">+{formatTon(w.payout)}</span>
            <span className="replay-row-cta">{loadingId === w.betId ? '…' : 'Повтор'}</span>
          </button>
        ))}
      </div>
    </>
  );
}

export function SlotsHub() {
  const [game, setGame] = useState<string | null>(null);
  const [replay, setReplay] = useState<ReplayResult | null>(null);

  // Повтор активен
  if (replay) {
    const back = () => setReplay(null);
    if (replay.game === 'olympus')
      return <OlympusScreen onBack={back} replay={replay.play} shareId={replay.betId} />;
    if (replay.game === 'doghouse')
      return <DogHouseScreen onBack={back} replay={replay.play} shareId={replay.betId} />;
    return (
      <>
        <div className="page-head">
          <button className="icon-btn" onClick={back} title="Назад">
            <Icon name="back" size={18} />
          </button>
          <span className="title">Повтор · Классика 777</span>
        </div>
        <SlotsScreen
          replay={{ base: replay.play.base, free: replay.play.free, totalWin: replay.play.totalWin }}
          shareId={replay.betId}
        />
      </>
    );
  }

  if (game === 'replays') return <ReplayList onBack={() => setGame(null)} onOpen={setReplay} />;
  if (game === 'olympus') return <OlympusScreen onBack={() => setGame(null)} />;
  if (game === 'doghouse') return <DogHouseScreen onBack={() => setGame(null)} />;
  if (game === 'classic')
    return (
      <>
        <div className="page-head">
          <button className="icon-btn" onClick={() => setGame(null)} title="Назад">
            <Icon name="back" size={18} />
          </button>
          <span className="title">Классика 777</span>
        </div>
        <SlotsScreen />
      </>
    );

  return (
    <div className="stack">
      <div className="section-title">Слоты</div>
      {GAMES.map((g) => (
        <button
          key={g.id}
          className={`slot-lobby-card ${g.ready ? '' : 'soon'}`}
          disabled={!g.ready}
          onClick={() => g.ready && setGame(g.id)}
          style={{ ['--accent2' as any]: g.accent }}
        >
          <span className="slot-lobby-emblem" style={{ background: g.accent }}>
            <Icon name="bolt" size={20} />
          </span>
          <span className="slot-lobby-info">
            <span className="slot-lobby-name">{g.name}</span>
            <span className="slot-lobby-desc">{g.desc}</span>
          </span>
          <span className="slot-lobby-cta">{g.ready ? 'Играть' : 'Скоро'}</span>
        </button>
      ))}

      <button className="slot-lobby-card replays-card" onClick={() => setGame('replays')}>
        <span className="slot-lobby-emblem" style={{ background: '#2b6cff' }}>
          <Icon name="back" size={20} />
        </span>
        <span className="slot-lobby-info">
          <span className="slot-lobby-name">Повторы выигрышей</span>
          <span className="slot-lobby-desc">Пересмотри и поделись своими спинами</span>
        </span>
        <span className="slot-lobby-cta">Открыть</span>
      </button>
    </div>
  );
}
